import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Store } from '../../services/store';
import { toVoid } from '../../utils/void';
import { normalizeAmount } from '../../utils/format-amount';
import { ClientPositionPickerProvider } from './client-position-picker.provider';
import { ClientPositionDto } from './models/client-position.dto';

export interface ClientPositionPickerState {
  loading: boolean;
  search: string;
  positions: ClientPositionVm[] | undefined;
}

/**
 * A bank row: the position with its unallocated remainder, the amount the picker
 * pre-fills — the row's input keeps what the user types (orders-new amount precedent).
 */
export interface ClientPositionVm {
  clientOrderItemId: number;
  name: string;
  unit: string;
  clientOrderId: number;
  orderNumber: string;
  clientName: string;
  deadline: string | null;
  remaining: number;
}

const INITIAL_STATE: ClientPositionPickerState = {
  loading: false,
  search: '',
  positions: undefined
};

// provided by ClientPositionPickerComponent, not root: every picker instance owns its state (DropdownStore precedent)
@Injectable()
export class ClientPositionPickerStore extends Store<ClientPositionPickerState> {
  constructor(private provider: ClientPositionPickerProvider) {
    super(INITIAL_STATE);
  }

  // the term follows the input at once; the fetch below is the debounced half
  setSearch(search: string): void {
    this.update(state => ({ ...state, search }));
  }

  /**
   * The bank is small and the whole point is seeing what is open — every search hits the
   * endpoint, the empty one included, so the list shows on open with no typing required.
   */
  fetch(search: string): Observable<void> {
    this.update(state => ({ ...state, loading: true }));

    return this.provider.searchClientPositions(search)
      .pipe(
        tap(positions => {
          this.update(state => ({
            ...state,
            loading: false,
            positions: positions.map(mapModelToViewModel)
          }));
        }),
        toVoid()
      );
  }
}

function mapModelToViewModel(position: ClientPositionDto): ClientPositionVm {
  return {
    clientOrderItemId: position.clientOrderItemId,
    name: position.name,
    unit: position.unit,
    orderNumber: position.orderNumber,
    clientOrderId: position.clientOrderId,
    clientName: position.clientName,
    deadline: position.deadline,
    remaining: normalizeAmount(position.amount - position.allocatedAmount)
  };
}
